import React from 'react';
import {Atom, Plugins, withMeta, withSystem} from '@packages/systems/wfdl';
import {
  TEmptyRecord,
  TListOfElement,
  TTypeConstructor,
  TUnionWithLabels,
} from '@packages/systems/wfdl-ast';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getStringOrElse,
  getTagOrElse,
} from '@packages/utilities/data-utils';
import {
  WebflowEnvironments,
  POPOVER_MODE_ATTRIBUTE,
} from '@packages/systems/core-constants';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {getPopoverElementDomId} from '@packages/systems/overlays';
import {
  POPOVER_MODE_AUTO,
  POPOVER_MODE_MANUAL,
  POPOVER_MODES,
  isPopoverMode,
} from '@packages/systems/plugins-utils/Popover';
import {
  DesignerOverlayTarget,
  type OverlayElement,
} from '@packages/systems/designer-canvas-overlays';

export type PopoverElement = OverlayElement;

const Popover = Atom.createRender(
  ({
    attributes,
    classes,
    data,
    elementId,
    environmentContext,
    generateAttributes,
    nativeIdPath,
    renderChild,
    context,
  }) => {
    const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
    const popoverMode = getInPojo(data, ['popover']);
    const popoverValue = getStringOrElse(
      getInPojo(popoverMode, ['tag']),
      getStringOrElse(popoverMode, POPOVER_MODE_AUTO)
    );
    const normalizedPopoverValue = isPopoverMode(popoverValue)
      ? popoverValue
      : POPOVER_MODE_AUTO;
    const runtimeEnvironment = useRuntimeEnvironment();
    const previewMode = usePreviewMode();
    const isDesignerAuthoringSurface =
      runtimeEnvironment === WebflowEnvironments.DESIGNER && !previewMode;
    const isLlmRender = runtimeEnvironment === WebflowEnvironments.LLM;
    const popoverDomId = getPopoverElementDomId(
      elementId,
      nativeIdPath,
      environmentContext
    );
    // The authored mode, kept where the runtime can still read it: the `popover`
    // attribute below is rewritten to `manual` while an animated popover plays
    // its close, so it stops reporting what the author chose.
    //
    // Not in the LLM render: no runtime reads it there, and the return trip
    // through `html-to-webflow` would turn it into an authored custom attribute
    // (`xattr`), so an otherwise unchanged AI edit would add one to every
    // popover it touched.
    let updatedAttributes = attributes.set('id', popoverDomId);
    if (!isLlmRender) {
      updatedAttributes = updatedAttributes.set(
        POPOVER_MODE_ATTRIBUTE,
        normalizedPopoverValue
      );
    }

    if (isDesignerAuthoringSurface) {
      updatedAttributes = updatedAttributes.set('popover', POPOVER_MODE_MANUAL);
    } else {
      updatedAttributes = updatedAttributes.set(
        'popover',
        normalizedPopoverValue
      );
    }

    const children = getChildren(data).map((child: EvaluatedNodeData) =>
      renderChild(child, context)
    );
    const renderTag = (ref: React.Ref<PopoverElement> | undefined) => (
      <Tag
        ref={ref}
        {...generateAttributes(updatedAttributes, classes.push('w-popover'))}
      >
        {children}
      </Tag>
    );

    if (runtimeEnvironment === WebflowEnvironments.DESIGNER) {
      return (
        <DesignerOverlayTarget
          data={data}
          elementId={elementId}
          environmentContext={environmentContext}
          nativeIdPath={nativeIdPath}
          selectedNativeIdPath={
            environmentContext.get('selectedNativeIdPath') as
              | readonly string[]
              | undefined
          }
          enabled={!previewMode}
          renderTag={renderTag}
        />
      );
    }

    return renderTag(undefined);
  }
);

const config = createDevlinkAtom({
  displayName: 'Popover',
  showEmpty: true,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Popover'), iconName('Popover')),
  },
  render: Popover,
  fields: {
    popover: withSystem(
      {label: 'Mode', defaultValue: {tag: POPOVER_MODE_AUTO}},
      TUnionWithLabels({
        [POPOVER_MODE_AUTO]: {label: POPOVER_MODES.auto, type: TEmptyRecord},
        [POPOVER_MODE_MANUAL]: {
          label: POPOVER_MODES.manual,
          type: TEmptyRecord,
        },
      })
    ),
    tag: withMeta(
      {variant: 'menu'},
      withSystem(
        {
          defaultValue: 'div',
          label: 'Tag',
          private: true,
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('TagsDropdown'))
        )
      )
    ),
    children: withSystem({private: true}, TListOfElement),
  },
});

export default config;
